import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api, ApiError, qs } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Panneau, Squelette, Vide } from '../components/ui';
import { FormulaireClient } from '../components/FormulaireClient';
import { dateFr, montant, nomClient } from '../lib/format';
import type { Client, Reservation, Verdict } from '../api/types';
import { T } from '../i18n';

export default function Clients() {
  const { peut } = useAuth();
  const [q, setQ] = useState('');
  const [liste, setListe] = useState<Client[] | null>(null);
  const [detail, setDetail] = useState<{ client: Client; verdict: Verdict; reservations: Reservation[] } | null>(null);
  const [nouveau, setNouveau] = useState(false);
  const [edite, setEdite] = useState<Client | null>(null);
  /* Mettre un client en liste noire n'est jamais un clic seul : le motif est
   * obligatoire cote serveur, et c'est lui qu'on relira au prochain refus. */
  const [listeNoire, setListeNoire] = useState<Client | null>(null);
  const [motifLN, setMotifLN] = useState('');
  const [envoiLN, setEnvoiLN] = useState(false);
  const [erreurLN, setErreurLN] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { api.get<Client[]>(`/clients${qs({ q })}`).then(setListe); }, 220);
    return () => clearTimeout(t);
  }, [q]);

  if (!liste) return <Squelette lignes={8} />;

  const tc = T.clients;
  return (
    <>
      <div className="entete">
        <div className="titres"><h1>{tc.titre}</h1><p>{liste.length === 0 && !q
          ? tc.sousTitreVide
          : tc.fiches(liste.length)}</p></div>
        <div className="actions"><button className="btn btn-p" onClick={() => setNouveau(true)}>{tc.nouveau}</button></div>
      </div>

      <Carte>
        <Champ label={tc.rechercher}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tc.rechercherExemple} />
        </Champ>
      </Carte>

      <div style={{ marginTop: 16 }}>
        <Carte pad={false}>
          {liste.length === 0 ? (
            <div className="carte-p">
              <Vide titre={q ? tc.aucunCorrespond : tc.aucuneFiche}
                    action={!q ? <button className="btn btn-p btn-l" onClick={() => setNouveau(true)}>{tc.premiere}</button> : undefined}>
                {q
                  ? <>{tc.autreOrthographe}</>
                  : <>{tc.videTexte}</>}
              </Vide>
            </div>
          ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead><tr><th>{tc.col.nom}</th><th>{tc.col.telephone}</th><th>{tc.col.piece}</th><th>{tc.col.permis}</th><th /></tr></thead>
              <tbody>
                {liste.map((c) => (
                  <tr key={c._id} style={{ cursor: 'pointer' }}
                      onClick={() => api.get<typeof detail>(`/clients/${c._id}`).then(setDetail)}>
                    <td>
                      <b>{nomClient(c)}</b>
                      {c.type === 'societe' && <span style={{ marginLeft: 6 }}><Badge statut="confirmee" libelle={tc.societe} /></span>}
                      {c.listeNoire && <span style={{ marginLeft: 6 }}><Badge statut="retard" libelle={tc.listeNoire} /></span>}
                      {c.compteATerme && <span style={{ marginLeft: 6 }}><Badge statut="retour" libelle={tc.compteATerme} /></span>}
                    </td>
                    <td className="num">{c.telephone}</td>
                    <td className="mini sourdine">{c.pieceType} {c.pieceNumero}</td>
                    <td>{dateFr(c.permisDelivreLe)}</td>
                    <td className="droite"><span className="btn btn-s">{tc.ouvrir}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </Carte>
      </div>

      {listeNoire && (
        <Modale titre={listeNoire.listeNoire
          ? tc.retirerTitre(nomClient(listeNoire))
          : tc.mettreTitre(nomClient(listeNoire))}
                onFermer={() => setListeNoire(null)}>
          <p className="mini sourdine" style={{ marginBottom: 12 }}>
            {listeNoire.listeNoire
              ? tc.retirerTexte
              : tc.mettreTexte}
          </p>
          {erreurLN && <div style={{ marginBottom: 12 }}><Bandeau type="ko">{erreurLN}</Bandeau></div>}
          <Champ label={tc.motif} aide={tc.motifAide}>
            <textarea rows={3} value={motifLN} onChange={(e) => setMotifLN(e.target.value)}
                      placeholder={listeNoire.listeNoire
                        ? tc.motifRetraitExemple
                        : tc.motifMiseExemple} />
          </Champ>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setListeNoire(null)}>{tc.annuler}</button>
            <button className={listeNoire.listeNoire ? 'btn btn-p' : 'btn btn-d'}
                    disabled={envoiLN || motifLN.trim().length < 3}
                    onClick={() => {
                      setEnvoiLN(true); setErreurLN(null);
                      api.post<Client>(`/clients/${listeNoire._id}/liste-noire`, {
                        listeNoire: !listeNoire.listeNoire, motif: motifLN.trim(),
                      })
                        .then((c) => {
                          setListeNoire(null);
                          setDetail(detail ? { ...detail, client: c } : null);
                          api.get<Client[]>(`/clients${qs({ q })}`).then(setListe);
                        })
                        .catch((e) => setErreurLN(e instanceof ApiError ? e.erreur : tc.enregistrementImpossible))
                        .finally(() => setEnvoiLN(false));
                    }}>
              {listeNoire.listeNoire ? tc.retirer : tc.mettre}
            </button>
          </div>
        </Modale>
      )}

      {edite && (
        <FormulaireClient client={edite}
          onFermer={() => setEdite(null)}
          onCree={(c, v) => {
            setEdite(null);
            setDetail(detail ? { ...detail, client: c, verdict: v } : null);
            api.get<Client[]>(`/clients${qs({ q })}`).then(setListe);
          }} />
      )}

      {nouveau && (
        <FormulaireClient onFermer={() => setNouveau(false)}
                          onCree={(c, v) => { setNouveau(false); setDetail({ client: c, verdict: v, reservations: [] }); setQ(c.nom); }} />
      )}

      {detail && (
        <Panneau titre={nomClient(detail.client)} onFermer={() => setDetail(null)}>
          {detail.client.listeNoire && <Bandeau type="ko" titre={tc.clientListeNoire}>{detail.client.motifListeNoire}</Bandeau>}

          {/*
            * Une date de naissance mal saisie bloque le client a l'article 01 a
            * chaque location, pour toujours. La demande de correction pouvait
            * etre accordee — mais rien ne permettait ensuite de changer la
            * valeur : la fiche etait en lecture seule partout.
            */}
          {peut('modifier.client') && (
            <div className="actions" style={{ marginTop: 12 }}>
              <button className="btn" onClick={() => setEdite(detail.client)}>{tc.modifierFiche}</button>
              <button className={detail.client.listeNoire ? 'btn' : 'btn btn-d'}
                      onClick={() => {
                        setMotifLN(detail.client.listeNoire ? '' : '');
                        setErreurLN(null);
                        setListeNoire(detail.client);
                      }}>
                {detail.client.listeNoire ? tc.retirer : tc.mettre}
              </button>
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <Carte titre={tc.article01}>
              {detail.verdict.conforme ? (
                <Bandeau type="ok" titre={tc.conforme}>
                  {tc.conformeTexte(detail.verdict.age, detail.verdict.ancienneteParmis)}
                </Bandeau>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {detail.verdict.blocages.map((b) => (
                    <Bandeau key={b.regle} type="ko" titre={b.message}>{b.detail}</Bandeau>
                  ))}
                </div>
              )}
            </Carte>
          </div>

          <div style={{ marginTop: 14 }}>
            <Carte titre={tc.coordonnees}>
              <table className="tbl compact">
                <tbody>
                  <tr><td>{tc.telephone}</td><td className="droite num">{detail.client.telephone}</td></tr>
                  <tr><td>{tc.email}</td><td className="droite">{detail.client.email ?? '—'}</td></tr>
                  <tr><td>{tc.adresse}</td><td className="droite">{detail.client.adresse ?? '—'}</td></tr>
                  <tr><td>{tc.naissance}</td><td className="droite">{dateFr(detail.client.dateNaissance)} {detail.client.lieuNaissance && tc.lieuNaissance(detail.client.lieuNaissance)}</td></tr>
                  <tr><td>{detail.client.pieceType}</td><td className="droite num">{detail.client.pieceNumero}</td></tr>
                  <tr><td>{tc.permis}</td><td className="droite">{detail.client.permisNumero ?? '—'} · {dateFr(detail.client.permisDelivreLe)}</td></tr>
                </tbody>
              </table>
            </Carte>
          </div>

          <div style={{ marginTop: 14 }}>
            <Carte titre={tc.historique(detail.reservations.length)} pad={false}>
              <table className="tbl compact">
                <thead><tr><th>{tc.colHisto.numero}</th><th>{tc.colHisto.vehicule}</th><th>{tc.colHisto.periode}</th><th className="droite">{tc.colHisto.total}</th><th /></tr></thead>
                <tbody>
                  {detail.reservations.map((r) => {
                    const v = r.vehicle as { marque?: string; modele?: string };
                    return (
                      <tr key={r._id}>
                        <td><Link to={`/reservations/${r._id}`}><b>{r.numero}</b></Link></td>
                        <td>{v?.marque} {v?.modele}</td>
                        <td className="mini sourdine">{dateFr(r.dateDebut)} → {dateFr(r.dateFin)}</td>
                        <td className="droite montant">{montant(r.totalPrevuMinor)}</td>
                        <td><Badge statut={r.statut} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Carte>
          </div>
        </Panneau>
      )}
    </>
  );
}
